import * as THREE from 'three';
import { bus } from '../core/EventBus';
import { PARTICLE_FRAGMENT, PARTICLE_VERTEX } from './shaders/particle.glsl';

/**
 * The soft round point material, shared by every point-sprite system in the effects stack.
 *
 * There are three of them — the pooled particles, the detonation flash, and the countermeasure
 * decoys — and all three were built on `THREE.PointsMaterial`, which draws a flat opaque square
 * and takes a single size for the whole system. Two consequences followed everywhere:
 *
 * 1. Every sprite was a hard-edged block, which is exactly what a fireball looked like once the
 *    impact cam finally framed one.
 * 2. Per-sprite size attributes were being written each frame and silently ignored, so a flash
 *    scaled by severity and a particle with a growth ramp both drew at a fixed size.
 *
 * Fixing this in one place rather than three is the point of the helper: the failure was
 * identical in each, and so is the remedy.
 *
 * The material owns its viewport uniform, because `gl_PointSize` is in framebuffer pixels and the
 * shader does its own perspective conversion — `PointsMaterial` gets that uniform from the
 * renderer for free and a raw `ShaderMaterial` does not.
 */

export interface SoftPoints {
  readonly material: THREE.ShaderMaterial;
  /** Detach the resize subscription. */
  dispose(): void;
}

export function createSoftPoints(options: { additive: boolean }): SoftPoints {
  const material = new THREE.ShaderMaterial({
    vertexShader: PARTICLE_VERTEX,
    fragmentShader: PARTICLE_FRAGMENT,
    uniforms: {
      // Framebuffer pixels, so this must follow the render scale as well as the device ratio.
      uViewportHeightPx: { value: 1080 },
    },
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    blending: options.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
  });

  const detach = bus.on('render:resize', (size) => {
    material.uniforms['uViewportHeightPx'].value = Math.max(1, size.heightPx);
  });

  return {
    material,
    dispose(): void {
      detach();
      material.dispose();
    },
  };
}

/**
 * Fill a colour attribute with one constant colour.
 *
 * The flash and the decoys are single-colour systems, but the shader reads vertex colours so it
 * can serve the multi-coloured particle pool with the same code. Writing the colour once at
 * construction is cheaper than a second shader variant.
 */
export function fillConstantColour(colours: Float32Array, colour: THREE.Color): void {
  for (let i = 0; i < colours.length; i += 3) {
    colours[i] = colour.r;
    colours[i + 1] = colour.g;
    colours[i + 2] = colour.b;
  }
}
